import React, { useEffect, useState } from 'react';
import Loader from "../../../components/Loader";
import Paginator from "../../../components/Paginator";
import ApiService from "../../../services/ApiService";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import DateRangePicker from "../../../components/DateRangePicker";
import { hasPermission } from "../../../utils/permissions";
import { formatDate } from "../../../utils/helper";
import './Broadcasts.css';

/**
 * Every notification sent or scheduled to app users.
 *
 * Delivery happens in the background, so rows that are still going out are
 * polled — the progress bar and counters move without a manual refresh.
 */

const STATUS_BADGE = {
    scheduled: 'badge-light-warning',
    processing: 'badge-light-info',
    completed: 'badge-light-success',
    cancelled: 'badge-light-dark',
    failed: 'badge-light-danger',
};

const CHANNEL_LABEL = {
    push: { label: 'Push', icon: 'bi bi-phone' },
    email: { label: 'Email', icon: 'bi bi-envelope' },
    both: { label: 'Push + Email', icon: 'bi bi-broadcast' },
};

const List = () => {

    const [isLoader, setIsLoader] = useState(false);
    const [broadcasts, setBroadcasts] = useState([]);
    const [pagination, setPagination] = useState({});
    const [apiData, setApiData] = useState({
        search: "",
        daterange: "",
        status: "",
        channel: "",
        page: 1,
    });

    const canSend = hasPermission('broadcasts.save');
    const canCancel = hasPermission('broadcasts.cancel');
    const canDelete = hasPermission('broadcasts.delete');
    const canView = hasPermission('broadcasts.show');

    useEffect(() => {
        getBroadcasts();
    }, [apiData.page, apiData.search, apiData.daterange, apiData.status, apiData.channel]); // Dependency array for re-fetching data

    // Anything mid-flight is still changing on the server; refresh until it settles.
    useEffect(() => {
        const inFlight = broadcasts.some(b => b.status === 'processing' || b.status === 'scheduled');
        if (!inFlight) return;
        const interval = setInterval(getBroadcasts, 10000);
        return () => clearInterval(interval);
    }, [broadcasts]); // eslint-disable-line react-hooks/exhaustive-deps

    const getBroadcasts = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "GET",
                url: "admin/broadcasts",
                data: apiData
            });
            const data = response.data;
            if (data.status) {
                setBroadcasts(data.data);
                setPagination(data.pagination);
            } else {
                toast.error(data.message);
            }
            setIsLoader(false);
        } catch (error) {
            setIsLoader(false);
        }
    };

    const handlePageChange = (event) => {
        setApiData(prev => ({ ...prev, page: event }));
    };

    const clearFilter = () => {
        setApiData({ search: '', daterange: '', status: '', channel: '', page: 1 });
    };

    const cancelData = async (id) => {
        const result = await Swal.fire({
            title: 'Cancel this notification?',
            text: 'Users already reached keep their notification; the rest will not be sent.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, cancel it',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });

        if (result.isConfirmed) {
            const response = await ApiService.request({
                method: "POST",
                url: `admin/broadcasts/${id}/cancel`,
            });
            const data = response.data;
            if (data.status) {
                getBroadcasts();
                toast.success(data.message);
            } else {
                toast.error(data.message);
            }
        }
    };

    const delData = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'you will not be able to revert it!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });

        if (result.isConfirmed) {
            const response = await ApiService.request({
                method: "DELETE",
                url: `admin/broadcasts/${id}`,
            });
            const data = response.data;
            if (data.status) {
                getBroadcasts();
                toast.success(data.message);
            } else {
                toast.error(data.message);
            }
        }
    };

    const progressOf = (item) => {
        const total = item.stats?.total || 0;
        if (!total) return 0;
        const done = (item.stats.sent || 0) + (item.stats.failed || 0) + (item.stats.skipped || 0);
        return Math.min(100, Math.round((done / total) * 100));
    };

    const isFiltered = apiData.search || apiData.daterange || apiData.status || apiData.channel;

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">{("Send Notifications")}</h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">{("Send Notifications")}</li>
                        </ul>
                    </div>
                    {canSend && (
                        <div className="d-flex align-items-center gap-2 gap-lg-3">
                            <Link to="/admin/broadcasts/create" className="btn btn-sm btn-base">
                                <i className="bi bi-send me-2"></i>New Notification
                            </Link>
                        </div>
                    )}
                </div>
            </div>
            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="w-100">
                                <form method="get">
                                    <div className="row">
                                        <div className="col-md-3">
                                            <div className="d-flex align-items-center position-relative my-1">
                                                <input type="text" value={apiData.search} onChange={e => setApiData({ ...apiData, search: e.target.value, page: 1 })} className="form-control form-control-solid me-5"
                                                    placeholder={("Search title, message..")} />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <DateRangePicker
                                                value={apiData.daterange}
                                                onChange={(daterange) => setApiData({ ...apiData, daterange, page: 1 })}
                                                placeholder="Select Date Range"
                                            />
                                        </div>
                                        <div className="col-md-2">
                                            <select className="form-select form-select-solid my-1" value={apiData.status}
                                                onChange={e => setApiData({ ...apiData, status: e.target.value, page: 1 })}>
                                                <option value="">All Status</option>
                                                <option value="scheduled">Scheduled</option>
                                                <option value="processing">Sending</option>
                                                <option value="completed">Completed</option>
                                                <option value="cancelled">Cancelled</option>
                                                <option value="failed">Failed</option>
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <select className="form-select form-select-solid my-1" value={apiData.channel}
                                                onChange={e => setApiData({ ...apiData, channel: e.target.value, page: 1 })}>
                                                <option value="">All Channels</option>
                                                <option value="push">Push</option>
                                                <option value="email">Email</option>
                                                <option value="both">Push + Email</option>
                                            </select>
                                        </div>
                                        <div className="col-md-2 mt-2">
                                            <button type="button" onClick={clearFilter} className={`btn btn-sm btn-secondary mx-1 ${!isFiltered ? 'd-none' : ''}`}>{('clear')}</button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                        <div className="card-body pt-0">
                            <div id="kt_broadcasts_table_wrapper" className="dataTables_wrapper dt-bootstrap4 no-footer">
                                <div className="table-responsive">
                                    <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer">
                                        <thead>
                                            <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                                <th>{("Notification")}</th>
                                                <th>{("Channel")}</th>
                                                <th>{("Audience")}</th>
                                                <th>{("Delivery")}</th>
                                                <th>{("Scheduled")}</th>
                                                <th>{("Status")}</th>
                                                <th>{("Actions")}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="fw-semibold text-gray-600 fs-6">
                                            {broadcasts.length > 0 ? broadcasts.map((item, index) => (
                                                <tr key={item._id || index}>
                                                    <td>
                                                        <div className="text-dark fw-bold">{item.title}</div>
                                                        <div className="text-muted fs-7 text-truncate" style={{ maxWidth: 320 }}>{item.body}</div>
                                                    </td>
                                                    <td>
                                                        <span className="badge badge-light-primary">
                                                            <i className={`${CHANNEL_LABEL[item.channel]?.icon} me-1`}></i>
                                                            {CHANNEL_LABEL[item.channel]?.label || item.channel}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {item.audience === 'all'
                                                            ? 'All users'
                                                            : `${item.user_ids?.length || 0} selected`}
                                                    </td>
                                                    <td style={{ minWidth: 160 }}>
                                                        <div className="d-flex justify-content-between fs-7 mb-1">
                                                            <span className="text-success">{item.stats?.sent || 0} sent</span>
                                                            {(item.stats?.failed > 0) && <span className="text-danger">{item.stats.failed} failed</span>}
                                                        </div>
                                                        <div className="bc-progress">
                                                            <div className="bc-progress-bar" style={{ width: `${progressOf(item)}%` }}></div>
                                                        </div>
                                                        <div className="fs-8 text-muted mt-1">
                                                            {item.stats?.total ? `of ${item.stats.total} recipients` : 'Not started'}
                                                        </div>
                                                    </td>
                                                    <td>{formatDate(item.scheduled_at, 'MMM D, YYYY hh:mm A')}</td>
                                                    <td>
                                                        <span className={`badge ${STATUS_BADGE[item.status] || 'badge-light-dark'} text-capitalize`}>
                                                            {item.status === 'processing' ? 'Sending' : item.status}
                                                        </span>
                                                    </td>
                                                    <td className="mt-2 text-nowrap">
                                                        {canView && (
                                                            <Link to={`/admin/broadcasts/${item._id}`} className="btn btn-sm h-35px btn-light-primary">
                                                                <i className="bi bi-eye-fill fs-6"></i>
                                                            </Link>
                                                        )}
                                                        {canCancel && ['scheduled', 'processing'].includes(item.status) && (
                                                            <button onClick={() => cancelData(item._id)} className="btn btn-sm h-35px btn-light-warning ms-2" title="Cancel">
                                                                <i className="bi bi-x-circle-fill fs-6"></i>
                                                            </button>
                                                        )}
                                                        {canDelete && item.status !== 'processing' && (
                                                            <button onClick={() => delData(item._id)} className="btn btn-sm h-35px btn-light-danger ms-2" title="Delete">
                                                                <i className="bi bi-trash-fill fs-6"></i>
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="7" className="text-dark text-center mt-2">No data found!!</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    {isLoader && (
                                        <Loader text="Loading" size="30" className="text-center" />
                                    )}

                                    {pagination.total_pages > 1 && (
                                        <Paginator
                                            page={pagination.current_page}
                                            rows={pagination.per_page}
                                            totalRecords={pagination.total_records}
                                            onPageChange={handlePageChange}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default List;
